import React from "react";

/**
 * LogoMarquee — a rectangular continuously-moving display of brand marks.
 * Uses simpleicons.org CDN for known platform marks (monochrome SVG), with
 * institutional fallback SVGs for courts and government bodies.
 *
 * Tone "sand" (default) renders on light bg; tone "ink" inverts.
 */
const SIMPLEICONS = {
  razorpay: "razorpay",
  phonepe: "phonepe",
  flipkart: "flipkart",
  zerodha: "zerodha",
  meesho: "meesho",
  cred: "cred",
  paytm: "paytm",
  policybazaar: "policybazaar",
  swiggy: "swiggy",
  zomato: "zomato",
  upstox: "upstox",
  groww: "groww",
};

// Institutional SVG marks for courts & government
const SealMarks = {
  scales: (color) => (
    <svg viewBox="0 0 64 64" width="48" height="48" aria-hidden="true">
      <path d="M32 8 L32 56" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M16 18 L48 18" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M16 18 L8 36 Q16 42 24 36 Z" fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M48 18 L40 36 Q48 42 56 36 Z" fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="26" y="54" width="12" height="3" fill={color} />
      <circle cx="32" cy="8" r="2.5" fill={color} />
    </svg>
  ),
  pillars: (color) => (
    <svg viewBox="0 0 64 64" width="48" height="48" aria-hidden="true">
      <path d="M6 18 L58 18" stroke={color} strokeWidth="2" />
      <path d="M8 14 L56 14" stroke={color} strokeWidth="2" />
      {[12, 24, 36, 48].map((x) => (
        <g key={x}>
          <rect x={x} y={20} width="4" height="32" fill="none" stroke={color} strokeWidth="1.4" />
        </g>
      ))}
      <path d="M4 54 L60 54" stroke={color} strokeWidth="2" />
      <path d="M6 58 L58 58" stroke={color} strokeWidth="1.4" />
    </svg>
  ),
  ashoka: (color) => (
    <svg viewBox="0 0 64 64" width="48" height="48" aria-hidden="true">
      <circle cx="32" cy="32" r="20" fill="none" stroke={color} strokeWidth="1.6" />
      {Array.from({ length: 24 }).map((_, i) => {
        const a = (i / 24) * Math.PI * 2;
        const x1 = 32 + Math.cos(a) * 6;
        const y1 = 32 + Math.sin(a) * 6;
        const x2 = 32 + Math.cos(a) * 19;
        const y2 = 32 + Math.sin(a) * 19;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.1" />;
      })}
      <circle cx="32" cy="32" r="4" fill={color} />
    </svg>
  ),
  shield: (color) => (
    <svg viewBox="0 0 64 64" width="48" height="48" aria-hidden="true">
      <path d="M32 6 L54 14 L54 32 Q54 50 32 58 Q10 50 10 32 L10 14 Z" fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M22 30 L30 38 L44 22" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

function LogoCard({ name, icon, caption, tone, fixedWidth = 220 }) {
  const ink = tone === "ink" ? "#EDE6D8" : "#15201C";
  const borderCol = tone === "ink" ? "rgba(237,230,216,0.18)" : "rgba(184,176,160,0.6)";
  return (
    <div
      className="shrink-0 flex flex-col items-center justify-between gap-3 px-6 py-6"
      style={{ width: fixedWidth, borderRight: `1px solid ${borderCol}` }}
    >
      <div className="h-12 flex items-center justify-center">
        {icon ? icon : (
          <img
            src={`https://cdn.simpleicons.org/${SIMPLEICONS[name.toLowerCase().replace(/\s+/g, "")] || name.toLowerCase()}/${tone === "ink" ? "EDE6D8" : "15201C"}`}
            alt={name}
            className="h-9 w-auto max-w-[140px] object-contain"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "block"; }}
          />
        )}
        {/* Wordmark fallback (hidden unless image fails) */}
        {!icon && (
          <div className="font-display text-[20px] hidden" style={{ color: ink, fontWeight: 500 }}>{name}</div>
        )}
      </div>
      <div className="font-mono text-[10px] tracking-[0.16em] uppercase" style={{ color: tone === "ink" ? "rgba(237,230,216,0.55)" : "rgba(21,32,28,0.55)" }}>
        {caption || name}
      </div>
    </div>
  );
}

export default function LogoMarquee({ items, tone = "sand", speed = 45, testid }) {
  const ink = tone === "ink" ? "#EDE6D8" : "#15201C";
  const borderCol = tone === "ink" ? "rgba(237,230,216,0.22)" : "rgba(184,176,160,0.6)";
  const bg = tone === "ink" ? "rgba(237,230,216,0.04)" : "rgba(255,255,255,0.45)";
  // Duplicate for seamless loop
  const loop = [...items, ...items];

  return (
    <div
      className="relative w-full overflow-hidden border"
      style={{ borderColor: borderCol, background: bg, borderRadius: 3 }}
      data-testid={testid}
    >
      {/* Edge fades */}
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-16 z-10"
        style={{ background: `linear-gradient(90deg, ${tone === "ink" ? "rgba(21,32,28,1)" : "rgba(237,230,216,1)"}, transparent)` }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-16 z-10"
        style={{ background: `linear-gradient(270deg, ${tone === "ink" ? "rgba(21,32,28,1)" : "rgba(237,230,216,1)"}, transparent)` }}
      />

      <div className="flex" style={{ animation: `esulah-marquee ${speed}s linear infinite`, willChange: "transform" }}>
        {loop.map((it, i) => (
          <LogoCard key={`${it.name}-${i}`} {...it} tone={tone} />
        ))}
      </div>

      <style>{`
        @keyframes esulah-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="${testid}"] > div[style*="esulah-marquee"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

LogoMarquee.SealMarks = SealMarks;
